import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getVoterPhotoUrl } from '../data/votingRepository';
import type { KioskVoter } from '../types';
import { CHANNEL_LABEL, KIND_BADGE, REASON_LABEL } from '../utils/labels';
import { MinisterPhoto } from './MinisterPhoto';

/** Photo of the person: profile photo of a minister, or the CI photo a guest uploaded */
export function VoterPhoto({ voter, electionId, className }: { voter: KioskVoter; electionId: string; className?: string }) {
  const [ciUrl, setCiUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setCiUrl(null);
    if (voter.kind === 'GUEST' && voter.has_ci_photo && voter.participant_id) {
      void getVoterPhotoUrl(electionId, voter.participant_id).then((u) => !cancelled && setCiUrl(u));
    }
    return () => {
      cancelled = true;
    };
  }, [voter, electionId]);

  if (ciUrl) return <img src={ciUrl} alt="CI" className={`rounded-lg object-cover ${className ?? 'size-24'}`} />;
  return <MinisterPhoto filename={voter.photo_filename} name={voter.full_name} className={className ?? 'size-24'} />;
}

export function VoterStatus({ voter }: { voter: KioskVoter }) {
  if (voter.voted) {
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-amber-700">
        <CheckCircle2 className="size-5" /> Ya votó {CHANNEL_LABEL[voter.voted.channel]}
      </span>
    );
  }
  if (!voter.eligible) {
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-destructive">
        <AlertTriangle className="size-5" /> {REASON_LABEL[voter.reason ?? ''] ?? 'No puede votar.'}
      </span>
    );
  }
  return <span className="font-medium text-green-700">Puede votar</span>;
}

export function VoterSummary({ voter }: { voter: KioskVoter }) {
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="truncate text-2xl font-bold">{voter.full_name}</p>
        <Badge variant="secondary">{KIND_BADGE[voter.kind]}</Badge>
      </div>
      <p className="text-lg text-muted-foreground">
        {[voter.ci && `CI ${voter.ci}`, voter.adb_code, voter.district_name].filter(Boolean).join(' · ')}
      </p>
    </div>
  );
}
